import {
  type CompleteSocialLoginBody,
  completeSocialLogin,
  continueSocialLogin,
  type NativeAuthFlowState,
  type NativeAuthTokenResponse,
  type PasswordLoginBody,
  passwordLogin,
  resendNativeLogin,
  StartNativeLoginBody,
  startNativeLogin,
  startSocialLogin,
  type VerifyNativeLoginBody,
  verifyNativeLogin,
} from '@echo/api-client/onearly';
import {
  isTerminalRefreshFailure,
  RefreshTokenRejectedError,
  RefreshUnavailableError,
} from './errors';
import type {
  AuthProvider,
  LoginFlow,
  SocialLoginStart,
  SocialProvider,
  StartLogin,
  Tokens,
} from './session';

/**
 * Core-brokered login — the same calls OnEarly `app`'s `AuthService` makes: Core drives
 * the Ory Kratos native flows and mints Ory OAuth tokens; refresh and revocation go to
 * Ory Hydra directly with the client Core issued the tokens for.
 *
 * Bodies are typed by the generated client, so a contract change fails typecheck here
 * rather than at runtime.
 */
export interface OnEarlyAuthOptions {
  /** `web` | `ios` | `android` — Core picks the matching OIDC provider for social login. */
  platform: 'web' | 'ios' | 'android';
  /** Ory `/oauth2/token`; `/oauth2/revoke` is derived from it. */
  tokenEndpoint: string;
  clientId: string;
  scopes: readonly string[];
  fetch?: typeof fetch;
}

export class OnEarlyAuthProvider implements AuthProvider {
  constructor(private readonly options: OnEarlyAuthOptions) {}

  /**
   * `homeRegion` is where a new account is kept, and Core answers `homeRegionRequired` (both
   * new with the second region, core#721). The region rides in the flow state, so it may be
   * set at start or on the flow sent to verify; Core records it at verify, for an account with
   * no home yet only. An older Core drops both.
   */
  async startLogin({ channel, identifier, homeRegion }: StartLogin): Promise<LoginFlow> {
    const region = contractRegion(homeRegion);
    const body: StartNativeLoginBody = {
      channel,
      identifier,
      ...(region ? { homeRegion: region } : {}),
    };
    return toFlow(ok(await startNativeLogin(body)));
  }

  /** A new code for the same flow; the region the person chose stays with it, for verify. */
  async resendLogin(flow: LoginFlow): Promise<LoginFlow> {
    const next = toFlow(ok(await resendNativeLogin(toFlowState(flow))));
    return flow.homeRegion && !next.homeRegion ? { ...next, homeRegion: flow.homeRegion } : next;
  }

  /** Core wants the whole flow state back, plus the six-digit code (and the chosen region). */
  async verifyLogin(input: { flow: LoginFlow; code: string }): Promise<Tokens> {
    const body: VerifyNativeLoginBody & SignInApp = {
      ...toFlowState(input.flow),
      code: input.code,
      app: SIGN_IN_APP,
    };
    return toTokens(ok(await verifyNativeLogin(body)));
  }

  async passwordLogin(input: { identifier: string; password: string }): Promise<Tokens> {
    const body: PasswordLoginBody & SignInApp = { ...input, app: SIGN_IN_APP };
    return toTokens(ok(await passwordLogin(body)));
  }

  async startSocialLogin(input: {
    provider: SocialProvider;
    redirectUri: string;
  }): Promise<SocialLoginStart> {
    const response = ok(
      await startSocialLogin({
        provider: input.provider,
        platform: this.options.platform,
        redirectUri: input.redirectUri,
      }),
    );
    return {
      url: response.redirectUrl,
      sessionTokenExchangeCode: response.sessionTokenExchangeCode,
    };
  }

  async completeSocialLogin(input: {
    sessionTokenExchangeCode: string;
    returnToCode: string;
  }): Promise<Tokens> {
    const body: CompleteSocialLoginBody & SignInApp = { ...input, app: SIGN_IN_APP };
    return toTokens(ok(await completeSocialLogin(body)));
  }

  async continueSocialLogin(input: { flowId: string }): Promise<LoginFlow> {
    return toFlow(ok(await continueSocialLogin(input)));
  }

  /** `grant_type=refresh_token` at Ory's token endpoint (RFC 6749 §6). */
  async refresh(tokens: Tokens): Promise<Tokens> {
    if (!tokens.refreshToken)
      throw new RefreshTokenRejectedError(undefined, 'missing_refresh_token');
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: tokens.refreshToken,
      client_id: this.clientIdFor(tokens),
      scope: this.options.scopes.join(' '),
    });
    let response: Response;
    try {
      response = await (this.options.fetch ?? fetch)(this.options.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body,
      });
    } catch (error) {
      throw new RefreshUnavailableError(undefined, error);
    }
    const data: unknown = await response.json().catch(() => undefined);
    if (!response.ok) {
      if (isTerminalRefreshFailure(response.status, data)) {
        throw new RefreshTokenRejectedError(response.status, readOAuthError(data));
      }
      throw new RefreshUnavailableError(response.status, data);
    }
    const record = asRecord(data);
    if (typeof record.access_token !== 'string' || typeof record.expires_in !== 'number') {
      // Ory answered 2xx with no token — treat as transient rather than sign anyone out.
      throw new RefreshUnavailableError(response.status, data);
    }
    return {
      accessToken: record.access_token,
      // Ory rotates refresh tokens; keep the old one only if none came back.
      refreshToken:
        typeof record.refresh_token === 'string' ? record.refresh_token : tokens.refreshToken,
      expiresAt: Date.now() + record.expires_in * 1000,
      idToken: typeof record.id_token === 'string' ? record.id_token : tokens.idToken,
      ...(tokens.clientId ? { clientId: tokens.clientId } : {}),
    };
  }

  /**
   * The client the tokens belong to: the one Core named when it minted them, else this
   * environment's OnEarly client, which minted every session before Core named it.
   */
  private clientIdFor(tokens: Tokens): string {
    return tokens.clientId ?? this.options.clientId;
  }

  /** RFC 7009 revocation of the refresh token — best effort, like `app`. */
  async signOut(tokens: Tokens | undefined): Promise<void> {
    if (!tokens?.refreshToken) return;
    const body = new URLSearchParams({
      token: tokens.refreshToken,
      token_type_hint: 'refresh_token',
      client_id: this.clientIdFor(tokens),
    });
    await (this.options.fetch ?? fetch)(this.options.tokenEndpoint.replace(/\/token$/, '/revoke'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    }).catch(() => undefined);
  }
}

// --- contract mapping -------------------------------------------------------

/**
 * Core mints Stronger Memory's tokens for Stronger Memory's own Ory client where it has
 * one, else for the OnEarly app's (core PRD-2355). Not in the vendored contract yet, and
 * an older Core ignores it.
 */
const SIGN_IN_APP = 'strongermemory';
type SignInApp = { app: typeof SIGN_IN_APP };

/** The mutator throws `ApiError` for non-2xx, so the data here is the 200 shape. */
function ok<T extends { status: number; data: unknown }>(
  response: T,
): Extract<T, { status: 200 }>['data'] {
  if (response.status !== 200) {
    throw new Error(`OnEarly auth: unexpected status ${response.status}`);
  }
  return response.data as Extract<T, { status: 200 }>['data'];
}

function toFlow(state: NativeAuthFlowState): LoginFlow {
  return state;
}

/**
 * The flow as the contract types it. Regions come from Core's sign-up list, so the app keeps
 * them as text; one the contract doesn't name yet is left out, and Core picks the home.
 */
function toFlowState({ homeRegion, ...flow }: LoginFlow): NativeAuthFlowState {
  const region = contractRegion(homeRegion);
  return region ? { ...flow, homeRegion: region } : flow;
}

function contractRegion(region: string | undefined): StartNativeLoginBody['homeRegion'] {
  const parsed = StartNativeLoginBody.shape.homeRegion.safeParse(region);
  return parsed.success ? parsed.data : undefined;
}

function toTokens(response: NativeAuthTokenResponse & { client_id?: unknown }): Tokens {
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    expiresAt: Date.now() + response.expires_in * 1000,
    idToken: response.id_token,
    // Which Ory client Core minted for (core PRD-2355); absent from an older Core.
    ...(typeof response.client_id === 'string' && response.client_id
      ? { clientId: response.client_id }
      : {}),
  };
}

function readOAuthError(data: unknown): string | undefined {
  const error = asRecord(data).error;
  return typeof error === 'string' ? error : undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}
